// Drill-down scope for the multi-family property pages.
//
// The data is a strict hierarchy — building → unit → household → resident —
// and every row already carries its parent's id. But no page read a parent id
// from the URL and BuildingsPage linked nowhere, so the hierarchy existed only
// in the database. Each level therefore needed its own top-level nav row to be
// reachable at all, which is how multi-family operators ended up with seven
// sibling rows for what is really one subject.
//
// See docs/ux-review/NAVIGATION_AUDIT.md.
//
// Two rules this exists to enforce:
//
//   - A filter that changes what is listed must be visible and reversible.
//     A deep link that silently narrows a list gets read as the whole list.
//   - It names the parent, not its id. "Elm Court · Unit 4B" is navigation;
//     "building_id=77777777…" is a debugging aid.

import { Link, useSearchParams } from 'react-router-dom';
import { Icon } from '@ng-unifi/ui';

export interface ScopeCrumb {
  /** Human name of the parent. Falls back to a short id when unresolved. */
  label: string;
  /** Where to go to see that parent. Omit for the current, non-clickable level. */
  href?: string;
}

export function usePropertyScope() {
  const [searchParams] = useSearchParams();
  return {
    buildingId: searchParams.get('building_id')?.trim() || null,
    unitId: searchParams.get('unit_id')?.trim() || null,
    householdId: searchParams.get('household_id')?.trim() || null,
  };
}

/**
 * The bar shown above a scoped list: where you are, how you got here, and the
 * way back out.
 */
export function PropertyScopeBar({
  crumbs,
  clearHref,
  countLabel,
}: {
  crumbs: ScopeCrumb[];
  /** Same page, no scope. */
  clearHref: string;
  /** e.g. "6 of 42 units" — a filtered count is not a total. */
  countLabel?: string;
}) {
  if (crumbs.length === 0) return null;
  return (
    <div
      role="status"
      className="flex flex-wrap items-center gap-x-2 gap-y-1 rounded-lg bg-surface-container px-4 py-3 font-body-md text-body-md text-on-surface-variant"
    >
      <Icon name="filter_alt" className="text-on-surface-variant" aria-hidden="true" />
      <span className="flex flex-wrap items-center gap-x-1">
        {crumbs.map((c, i) => (
          <span key={`${c.label}-${i}`} className="flex items-center gap-1">
            {i > 0 ? <span aria-hidden="true">·</span> : null}
            {c.href ? (
              <Link
                to={c.href}
                className="rounded-sm text-primary hover:underline focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary"
              >
                {c.label}
              </Link>
            ) : (
              <strong className="font-medium text-on-surface">{c.label}</strong>
            )}
          </span>
        ))}
      </span>
      {countLabel ? <span className="text-on-surface-variant">· {countLabel}</span> : null}
      <Link
        to={clearHref}
        className="ml-auto rounded-sm text-primary underline hover:no-underline focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary"
      >
        Show all
      </Link>
    </div>
  );
}

/** `/units?building_id=…` and friends, with the parent id encoded. */
export function scopedHref(base: string, params: Record<string, string | null | undefined>): string {
  const q = new URLSearchParams();
  for (const [k, v] of Object.entries(params)) {
    if (v) q.set(k, v);
  }
  const qs = q.toString();
  return qs ? `${base}?${qs}` : base;
}
